import Cookies from 'universal-cookie';
import { Config, TokenManager, UserManager, FRUser } from '@forgerock/javascript-sdk';
import jwtDecodeLib from 'jwt-decode';

/**
 *  @param {boolean} isDevelopment (Optional)  set to true if running locally. Could be used with NODE_ENV.
*/
const getRedirectUri = isDevelopment => {
  const loc = window.location;
  const port = isDevelopment ? `:${loc.port}` : "";
  return `${loc.protocol}//${loc.hostname}${port}/account/callback`;
};

/* istanbul ignore file */
const TEN_MINS_MS = 600000;
const SDK_TIMEOUT = 30000;
const FIVE_MINS_MS = 300000;
const ID_TOKEN_EXPIRY_TIME = 300;
const MILLISECONDS = 1000;
// 12 mins in milliseconds
const EXPIRATION_DEV = 720000;
// 420 (nice) days in milliseconds
const EXPIRATION_PROD = 604800000;
const ID_TOKEN = "idToken";
const FORGEROCK_USER = "forgerockUser";
const CURRENT_SHIPPING = "currentshipping";
const CURRENT_BILLING = "currentbilling";
const SDK_CLIENT_KEY = "forgerock-sdk-arcSdkClient";
const defaultScope = "openid profile me.read me.update me.update-password email address phone arcprofile read write edit fr:idm:*";
const defaultRealm = "alpha";
const ARC_SAVE_ADDRESS = "arcSaveAddress";
const CUST_TYPE = "custType";
const CUST_NO = "custPrimaryContactNo";
const CUST_IS_IND = "custIndustry";
const CUST_DISC = "custDisc";
const CUST_IS_PRO = "custIsPro";
const CUST_EMAIL = "custEmail";
const customerDiscountByMarket = [{
  market: "outdoor",
  custDisc: [{
    groupType: "PR-1",
    value: 40
  }, {
    groupType: "PR-2",
    value: 50
  }, {
    groupType: "PR-3",
    value: 60
  }, {
    groupType: "LX-1",
    value: 40
  }, {
    groupType: "LX-2",
    value: 60
  }, {
    groupType: "IN-1",
    value: 25
  }, {
    groupType: "IN-2",
    value: 40
  }, {
    groupType: "IN-3",
    value: 50
  }, {
    groupType: "IN-4",
    value: 60
  }]
}, {
  market: "outlet",
  custDisc: [{
    groupType: "PR-1",
    value: 50
  }, {
    groupType: "PR-2",
    value: 55
  }, {
    groupType: "PR-3",
    value: 65
  }, {
    groupType: "LX-1",
    value: 45
  }, {
    groupType: "LX-2",
    value: 65
  }, {
    groupType: "IN-1",
    value: 35
  }, {
    groupType: "IN-2",
    value: 50
  }, {
    groupType: "IN-3",
    value: 55
  }, {
    groupType: "IN-4",
    value: 65
  }]
}];
const purchaseLimitCodeMap = ["at", "be", "ca", "cz", "de", "es", "fi", "fr", "ie", "it", "nl", "us"];
const AuthConstants = {
  TEN_MINS_MS,
  SDK_TIMEOUT,
  FIVE_MINS_MS,
  ID_TOKEN_EXPIRY_TIME,
  MILLISECONDS,
  EXPIRATION_DEV,
  EXPIRATION_PROD,
  ID_TOKEN,
  FORGEROCK_USER,
  CURRENT_SHIPPING,
  CURRENT_BILLING,
  SDK_CLIENT_KEY,
  defaultScope,
  defaultRealm,
  ARC_SAVE_ADDRESS,
  CUST_TYPE,
  CUST_NO,
  CUST_IS_IND,
  CUST_DISC,
  CUST_IS_PRO,
  CUST_EMAIL,
  customerDiscountByMarket,
  purchaseLimitCodeMap
};

// We only support 3 currencies when determining remaining balance
const currencyCodeMap = {
  us: "USD",
  ca: "CAD",
  gb: "CAD",
  default: "EUR"
};
const getCurrencyCodeForCountry = country => currencyCodeMap[country] || currencyCodeMap.default;

/* remainingBalanceArr from the JWT idToken should have a value of
*   remainingBalance": [
      "USD:2500",
      "EUR:2500",
      "CAD:3000"
    ]
**/
const getRemainingBalanceFromArray = function (country) {
  let remainingBalanceArr = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  const currencyCode = getCurrencyCodeForCountry(country.toLowerCase());
  for (const itemArr of remainingBalanceArr) {
    const [code, balance] = itemArr.split(":");
    if (code === currencyCode) {
      return parseFloat(balance);
    }
  }
  return null;
};

var _window2;
const jwtDecode = jwt => {
  try {
    return jwtDecodeLib(jwt);
  } catch (_unused) {
    return null;
  }
};
const cookies = new Cookies();
const adaptConfig = config => {
  const sdkConfig = {
    clientId: config.sdkClientId,
    redirectUri: config.redirectUri || getRedirectUri(config.development),
    scope: config.scope || defaultScope,
    serverConfig: {
      baseUrl: `${config.sdkBaseUrl}/am`,
      timeout: SDK_TIMEOUT
    },
    realmPath: config.sdkRealmPath || defaultRealm,
    tokenStore: config.tokenStore || "localStorage",
    tree: config.sdkTree
  };
  if (config.paths) {
    sdkConfig.serverConfig.paths = config.paths;
  }
  return sdkConfig;
};

/**
 *  @param {object}   config  Configuration parameters
 *  @param {string}   config.env          (Required)  the application environment (e.g. qa, stage, prod)
 *  @param {string}   config.country      (Required)  the country code (e.g. ca, us, gb, fr)
 *  @param {string}   config.language     (Required)  the language code (e.g. en, fr, de, es)
 *  @param {string}   config.sdkBaseUrl   (Required)  the base url to Forgerock AM
 *  @param {string}   config.sdkiClientId (Required)  the Forgerock SDK Client Id
 *  @param {string}   config.sdkRealmPath (Required)  the realm or realm path in which the authentication tree is configured
 *  @param {string}   config.sdkTree      (Required)  the name of the authentication tree to use
 *  @param {string}   config.scope        (Optional)  space-separated list of scopes to request when performing an OAuth 2.0 authorization flow
 *  @param {string}   config.redirectUri  (Optional)  the redirect_uri as configured in the OAuth 2.0 sdkiClientId
 *  @param {object}   config.paths        (Optional)  override the default paths for endpoints { authenticate, authorize, accessToken, revoke, userInfo, sessions, endSession }
 *  @param {string}   config.tokenStore   (Optional)  the tokenStore is where the tkens will be stored (indexedDB, sessionStorage, localStorage)
 *  @param {boolean}  config.development  (Optional)  set to true if running locally. Could be used with NODE_ENV.
 */
const initAuthentication = config => {
  Config.set(adaptConfig(config));
};

/**
 *  @param {object}   config Configuration parameters
 *  @param {boolean}  config.forceRenew   (optional)  Determines whether to get new tokens vs immediately returning stored tokesn if they exist
 *  @param {string}   config.login        (optional)  'embedded' = if user authentication is handled in-app
 *                                                    'redirect' = user authentication is handled in external Web app
 *                                                     undefined
 */
const getTokens = async config => await TokenManager.getTokens(config);
const getUser = async () => await UserManager.getCurrentUser();
const fetchPatchJson = async (config, headers, data) => {
  try {
    const response = await fetch(`${config.baseUrl}/openidm/managed/${config.realm}_user/${config.id}`, {
      method: "PATCH",
      headers,
      body: JSON.stringify(data)
    });
    const json = await response.json();
    return json;
  } catch (error) {
    console.error(error);
  }
  return null;
};

/**
 *  @param config {Object}  Configuration parameters
 *  @param {string}  config.baseUrl      (Required)  the base url to Forgerock AM
 *  @param {string}  config.realm        (Required)  the realm or realm path in which the authentication tree is configured
 *  @param {string}  config.accessToken  (Required)  the accessToken from an authenticated user
 *  @param {string}  config.id           (Required)  the ForgeRock id of the user
 */
const getUserProfile = async config => {
  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${config.accessToken}`
  };
  try {
    const response = await fetch(`${config.baseUrl}/openidm/managed/${config.realm}_user/${config.id}`, {
      headers
    });
    const json = await response.json();
    return json;
  } catch (error) {
    console.error(error);
  }
  return null;
};

/**
 *  @param {object}  config  Configuration parameters
 *  @param {string}  config.baseUrl      (Required)  the base url to Forgerock AM
 *  @param {string}  config.realm        (Required)  the realm or realm path in which the authentication tree is configured
 *  @param {string}  config.accessToken  (Required)  the accessToken from an authenticated user
 *  @param {string}  config.id           (Required)  the ForgeRock id of the user
 *  @param {oject}   data                (Required)  the array of form data objects to save user profile e.g. [{ field: "fieldName", value: "newValue"}]
 */
const saveUserProfile = async (config, data) => {
  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${config.accessToken}`
  };
  return await fetchPatchJson(config, headers, data);
};

/**
 *  @param {object}  config  Configuration parameters
 *  @param {string}  config.baseUrl      (Required)  the base url to Forgerock AM
 *  @param {string}  config.realm        (Required)  the realm or realm path in which the authentication tree is configured
 *  @param {string}  config.accessToken  (Required)  the accessToken from an authenticated user
 *  @param {string}  config.id           (Required)  the ForgeRock id of the user
 *  @param {oject}   newPassword         (Required)  the new password
 */
const changeUserPassword = async (config, newPassword) => {
  const data = [{
    operation: "add",
    field: "password",
    value: newPassword
  }];
  const headers = {
    "Content-Type": "application/json",
    "X-Requested-With": "XMLHttpRequest",
    Authorization: `Bearer ${config.accessToken}`
  };
  return await fetchPatchJson(config, headers, data);
};
const hasLocalStorage = () => {
  var _window;
  return typeof window !== "undefined" && ((_window = window) === null || _window === void 0 ? void 0 : _window.localStorage);
};
// eslint-disable-next-line prefer-destructuring
const sessionStorage = typeof window !== "undefined" ? (_window2 = window) === null || _window2 === void 0 ? void 0 : _window2.sessionStorage : null;
const getIdToken = () => {
  let jwtIdToken;
  if (hasLocalStorage() && localStorage.getItem(SDK_CLIENT_KEY)) {
    jwtIdToken = JSON.parse(localStorage.getItem(SDK_CLIENT_KEY)).idToken;
  }
  return jwtIdToken || "";
};
const getDecodedJwt = () => {
  if (getIdToken() !== "") {
    return jwtDecode(getIdToken());
  }
  return null;
};
const isGroup = groupPrefix => {
  const decodeJwt = getDecodedJwt();
  return decodeJwt === null || decodeJwt === void 0 ? void 0 : decodeJwt.groupId.includes(groupPrefix);
};
const isProUser = () => isGroup("PR");
const isIndustryUser = () => isGroup("IN");
const isLeafUser = () => isGroup("LX");
const isArcStaff = () => isGroup("AR");
const isAmerStaff = () => isGroup("AM");
const isRegularUser = () => isGroup("CO");
const isStaffUser = () => isArcStaff() || isAmerStaff();
const getGroupIdFromJwt = jwt => {
  const decodeJwt = jwtDecode(jwt);
  return (decodeJwt === null || decodeJwt === void 0 ? void 0 : decodeJwt.groupId) || "CO-1";
};
const getForgeRockUser = () => hasLocalStorage() ? localStorage.getItem(FORGEROCK_USER) : null;
const remainingBalance = country => {
  try {
    const decodeJwt = getDecodedJwt();
    const balance = getRemainingBalanceFromArray(country, decodeJwt === null || decodeJwt === void 0 ? void 0 : decodeJwt.remainingBalance);
    return balance;
  } catch (_unused2) {
    return null;
  }
};
const getAuthenticatedUser = () => {
  if (getForgeRockUser()) {
    return JSON.parse(getForgeRockUser());
  }
  return null;
};
const setForgeRockUser = userData => hasLocalStorage() ? localStorage.setItem(FORGEROCK_USER, JSON.stringify(userData)) : null;
const discountValueByGroupType = (market, userGroupId) => {
  const selectedCustomerMarket = customerDiscountByMarket.find(item => item.market === market);
  const disc = selectedCustomerMarket === null || selectedCustomerMarket === void 0 ? void 0 : selectedCustomerMarket.custDisc.find(item => item.groupType === userGroupId);
  return disc === null || disc === void 0 ? void 0 : disc.value.toString();
};
const setSessionStorage = forgerRockUser => {
  if (typeof forgerRockUser === "object" && forgerRockUser !== null) {
    var _forgerRockUser$marke;
    sessionStorage.setItem(CUST_TYPE, forgerRockUser === null || forgerRockUser === void 0 ? void 0 : forgerRockUser.groupPrefix);
    sessionStorage.setItem(CUST_NO, forgerRockUser === null || forgerRockUser === void 0 ? void 0 : forgerRockUser.uuid);
    sessionStorage.setItem(CUST_IS_IND, (forgerRockUser === null || forgerRockUser === void 0 ? void 0 : forgerRockUser.groupPrefix) === "IN");
    sessionStorage.setItem(CUST_DISC, discountValueByGroupType(forgerRockUser === null || forgerRockUser === void 0 ? void 0 : (_forgerRockUser$marke = forgerRockUser.market) === null || _forgerRockUser$marke === void 0 ? void 0 : _forgerRockUser$marke.toLowerCase(), forgerRockUser === null || forgerRockUser === void 0 ? void 0 : forgerRockUser.groupId));
    sessionStorage.setItem(CUST_IS_PRO, (forgerRockUser === null || forgerRockUser === void 0 ? void 0 : forgerRockUser.groupPrefix) === "PR");
    sessionStorage.setItem(CUST_EMAIL, forgerRockUser === null || forgerRockUser === void 0 ? void 0 : forgerRockUser.email);
  }
};

/**
 *
 * @param {*} [jwt]
 * @param {*} [expiryTime]
 * @returns {boolean}
 */
const isIdTokenExpired = function () {
  let jwt = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
  let expiryTime = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : ID_TOKEN_EXPIRY_TIME;
  try {
    const decodedJwt = jwt ? jwtDecode(jwt) : getDecodedJwt();
    if (decodedJwt !== null && decodedJwt !== void 0 && decodedJwt.exp) {
      const expiry = (decodedJwt.exp - expiryTime) * MILLISECONDS; // set expire to 10mins (default) before it expires
      const now = Date.now();
      return now > expiry;
    }
    return true; // If cannot find expiry from jwt, then consider it expired
  } catch (e) {
    // If jwt is not valid, we'll consider it expired
    console.error("Error decoding IdToken", e);
    return true;
  }
};

/**
 *  This function can be used to determine if the authenticated Pro, Industry, or Staff user
 *  has their discount disabled. It will check against the JWT idToken that is stored in locatStorage
 *  after a user is logged in.
 *
 *  @returns {boolean}
 */
const isDiscountDisabled = () => {
  if (getIdToken()) {
    try {
      const decodedIdToken = jwtDecode(getIdToken());
      return !isRegularUser() && (decodedIdToken === null || decodedIdToken === void 0 ? void 0 : decodedIdToken.disableDiscount) === "1";
    } catch (e) {
      console.error("Error decoding IdToken", e);
      return false;
    }
  }
  return false;
};

/**
 *  @description clear all user data and update header/footer
 *  @param {Element} [headerElement] the header/footer element
 *  @param {string}  [market] outdoor | sale | leaf
 */
const clearAuthenticatedUserFromHeaderFooter = (headerElement, market) => {
  if (Boolean(headerElement) && Boolean(market)) {
    var _headerElement$queryS, _headerElement$queryS2, _headerElement$queryS3, _headerElement$queryS4;
    const selector = `.${market.toUpperCase()}`;
    (_headerElement$queryS = headerElement.querySelector(selector)) === null || _headerElement$queryS === void 0 ? void 0 : (_headerElement$queryS2 = _headerElement$queryS.classList) === null || _headerElement$queryS2 === void 0 ? void 0 : _headerElement$queryS2.add("NOTLOGGEDIN");
    (_headerElement$queryS3 = headerElement.querySelector(selector)) === null || _headerElement$queryS3 === void 0 ? void 0 : (_headerElement$queryS4 = _headerElement$queryS3.classList) === null || _headerElement$queryS4 === void 0 ? void 0 : _headerElement$queryS4.remove("LOGGEDIN");
  }
  localStorage.removeItem(ARC_SAVE_ADDRESS);
  localStorage.removeItem(SDK_CLIENT_KEY);
  localStorage.removeItem(FORGEROCK_USER);
  localStorage.removeItem(CURRENT_SHIPPING);
  localStorage.removeItem(CURRENT_BILLING);
  sessionStorage.removeItem(CUST_TYPE);
  sessionStorage.removeItem(CUST_NO);
  sessionStorage.removeItem(CUST_IS_IND);
  sessionStorage.removeItem(CUST_DISC);
  sessionStorage.removeItem(CUST_IS_PRO);
  sessionStorage.removeItem(CUST_EMAIL);

  // Clear cart data
  localStorage.removeItem("CartId-OUTDOOR-");
  localStorage.removeItem("CartId-SALE-");
  cookies.remove("CartCount-OUTDOOR", {
    path: "/"
  });
  cookies.remove("CartCount-SALE", {
    path: "/"
  });

  // Update the footer
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("update-user-auth"));
  }
};

/**
 *  @description logout user and clear all their data
 *  @param {string} [redirectUrl] the URL to redirect after a user logs out
 *  @param {Element} [headerElement] the URL to redirect after a user logs out
 *  @param {string} [market] the URL to redirect after a user logs out
 */
const logout = async (redirectUrl, headerElement, market) => {
  try {
    clearAuthenticatedUserFromHeaderFooter(headerElement, market);
    await FRUser.logout();
  } catch (error) {
    console.error("Error logging out", error);
  } finally {
    if (redirectUrl) {
      window.location.href = redirectUrl;
    }
  }
};

/**
 *  @description tries to renew tokens for user, if it fails it logs-out the user
 *
 *  @param {Object} RenewTokensParams
 *  @param {Element} RenewTokensParams.headerElement (Required)  the header element
 *  @param {string}  RenewTokensParams.market    (Required)  outdoor | sale | leaf
 *  @param {boolean} RenewTokensParams.renew (optional) Get new tokens, despite having existing ones. This will remove existing tokens first.
 */
async function renewTokens(_ref) {
  let {
    headerElement,
    market,
    renew = false
  } = _ref;
  if (getIdToken()) {
    // Refresh tokens every 10mins to keep session alive
    try {
      await getTokens({
        forceRenew: renew
      });
    } catch (e) {
      console.error("Error refreshing idToken", e);
      logout(undefined, headerElement, market);
    }
  }
}

/**
 *  @description Refreshes the access and id tokens every few minutes
 *  renewTokens cannot be called immediately as it will fail and logout the user
 *
 *  @param {Object} RefreshIdParams
 *  @param {Element} [RefreshIdParams.headerElement] the header element
 *  @param {string}  [RefreshIdParams.market] outdoor | sale | leaf
 *  @param {boolean} [RefreshIdParams.renew] Get new tokens, despite having existing ones. This will remove existing tokens first.
 *
 *  @returns {NodeJS.Timeout} handle to clear the interval
 */
const refreshIdToken = _ref2 => {
  let {
    headerElement,
    market,
    renew = false
  } = _ref2;
  return setInterval(() => {
    renewTokens({
      headerElement,
      market,
      renew
    });
  }, TEN_MINS_MS);
};

/**
 * @description Check if the user is authenticated
 * @returns {boolean}
 */
const isAuthenticated = () => Boolean(getForgeRockUser()) && !isIdTokenExpired();

/**
 *
 * @param {string} country
 * @returns Check to see if it's one of the permitted countries. Otherwise,
 *  because EUR is the default currency, de will be assigned as the default country.
 */
const purchaseLimitCountry = country => {
  return purchaseLimitCodeMap.includes(country) ? country : "de";
};
const getPurchaseLimitData = async (country, jwt, url) => {
  try {
    const response = await fetch(url, {
      method: "POST",
      body: JSON.stringify({
        country,
        jwtToken: jwt
      })
    });
    if (!response.ok) {
      throw new Error(`HTTP error: ${response.statusText} when fetching ${url}`);
    }
    const json = await response.json();
    const {
      data
    } = json;
    return data;
  } catch (error) {
    console.error(error);
    return null;
  }
};
const adaptFormData = data => {
  const formData = [];
  Object.keys(data).forEach(key => {
    const obj = {
      field: key,
      value: data[key]
    };
    formData.push(obj);
  });
  return formData;
};
const getAccessToken = SDK_CLIENT_KEY => {
  try {
    var _JSON$parse;
    const localStorageSdkKey = (_JSON$parse = JSON.parse(localStorage.getItem(SDK_CLIENT_KEY))) === null || _JSON$parse === void 0 ? void 0 : _JSON$parse.accessToken;
    return localStorageSdkKey;
  } catch (error) {
    return null;
  }
};

export { AuthConstants, adaptConfig, adaptFormData, changeUserPassword, clearAuthenticatedUserFromHeaderFooter, getAccessToken, getAuthenticatedUser, getCurrencyCodeForCountry, getDecodedJwt, getForgeRockUser, getGroupIdFromJwt, getIdToken, getPurchaseLimitData, getRemainingBalanceFromArray, getTokens, getUser, getUserProfile, initAuthentication, isAmerStaff, isArcStaff, isAuthenticated, isDiscountDisabled, isIdTokenExpired, isIndustryUser, isLeafUser, isProUser, isRegularUser, isStaffUser, logout, purchaseLimitCountry, refreshIdToken, remainingBalance, saveUserProfile, setForgeRockUser, setSessionStorage };
//# sourceMappingURL=index.js.map
